<template>
    <section
        :id="slice.primary.anchorName || undefined"
        :class="[
            'slice slice-form slice-form--default',
            `slice-form--${theme}`,
            {'slice-form--hide-in-mobile': slice.primary.hideInMobile},
            {'slice-form--malt-strategy': slice.primary.maltStrategyTheme},
        ]"
        aria-label="form"
        data-testid="slice-form-default"
    >
        <div class="slice-content">
            <svg
                v-if="hasEmbeddedShapes"
                class="slice-form__bg-shape"
                :class="{'slice-form__bg-shape--malt-strategy': slice.primary.maltStrategyTheme}"
                width="1315"
                height="747"
                viewBox="0 0 1315 747"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
            >
                <path :d="pathS" class="slice-form__bg-shape__s" />
                <path :d="pathM" class="slice-form__bg-shape__m" />
                <path :d="pathL" class="slice-form__bg-shape__l" />
            </svg>
            <div class="slice-form__container" :class="{'slice-form__container--ready': formReady}">
                <div v-if="theme === 'ai'" class="slice-form__bg-gradient">
                    <div class="slice-form__bg-gradient__left" />
                    <div class="slice-form__bg-gradient__right" />
                </div>

                <div class="slice-form__card">
                    <MktBrandTitle v-if="theme === 'primary'" :field="slice.primary.title" data-testid="slice-form-title" />
                    <PrismicRichText v-else :field="slice.primary.title" class="slice-title" data-testid="slice-form-title" />
                    <div
                        v-if="Array.isArray(slice.primary.description) && isFilled.richText(slice.primary.description)"
                        class="slice-description"
                        data-testid="slice-form-description"
                    >
                        <PrismicRichText :field="slice.primary.description" />
                    </div>
                    <div data-testid="slice-form-hubspot">
                        <MktHubspotForm
                            :portal-id="slice.primary.hubspotPortalId + ''"
                            :form-id="slice.primary.hubspotFormId + ''"
                            :region="slice.primary.hubspotRegion + ''"
                            :salesforce-id="slice.primary.salesforceId + ''"
                            @submit="onFormSubmit"
                            @ready="onFormReady"
                        />
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>

<script setup lang="ts">
    import {inject, ref, watch} from 'vue';
    import {isFilled} from '@prismicio/helpers';
    import {getSliceComponentProps, PrismicRichText} from '@prismicio/vue';
    import type {FormSlice} from '@prismicio-types';
    import {storeToRefs} from 'pinia';
    import {MktBrandTitle, MktHubspotForm} from '#components';
    import {useAuth, useDataTracking} from '#imports';

    type FormSliceDefaultInZone = Extract<FormSlice, {variation: 'default'}>;
    const props = defineProps(getSliceComponentProps<FormSliceDefaultInZone>());
    const {slice} = props;

    const {track, extractWebAttributionProperties} = useDataTracking();
    const authStore = useAuth();
    const {user} = storeToRefs(authStore);

    const pageCategory = inject('pageCategory');

    const formElement = ref<HTMLFormElement>();
    const formReady = ref(false);
    const theme = ref(slice.primary.maltStrategyTheme ? 'strategy' : slice.primary.theme || 'default');

    const hasEmbeddedShapes = !['ai', 'primary'].includes(theme.value);

    watch([user, formReady], () => {
        if (formReady.value && user.value) {
            if (user.value.username) {
                const splitedUsername = user.value.username.split(' ');
                if (splitedUsername.length >= 2) {
                    updateInputFieldIfExists(formElement.value, 'input[name="firstname"]', splitedUsername.shift());
                    updateInputFieldIfExists(formElement.value, 'input[name="lastname"]', splitedUsername.join(' '));
                }
            }

            updateInputFieldIfExists(formElement.value, 'input[name="email"]', user.value.email);

            const maybeSelectedClientIdentity = user.value.identities.find(
                (identity) => identity.identityType === 'CLIENT' && identity.id === user.value!.selectedIdentity,
            );
            if (maybeSelectedClientIdentity) {
                updateInputFieldIfExists(formElement.value, 'input[name="company"]', maybeSelectedClientIdentity.clientCompanyName);
            }
        }
    });

    function onFormReady(form: HTMLFormElement) {
        formReady.value = true;
        formElement.value = form;
    }
    function onFormSubmit(form: HTMLFormElement) {
        const h1 = document.querySelector('h1');
        const typeField = form.querySelector(`.hs-input[name=form_type]`) as HTMLInputElement;
        const eventProperties = {
            url: window.location.pathname,
            page_title_h1: h1?.innerText,
            page_category: pageCategory,
            form_type: typeField?.value || null,
            form_name: form.dataset.formId,
        };
        const userProperties = {
            ...extractWebAttributionProperties(),
            referrer: document.referrer || null,
        };
        track('website_form_submitted', eventProperties, userProperties);
    }

    const pathS =
        'M-63.8987 32.2517C-52.4161 -4.18935 -18.7951 -2.02382 5.52242 5.64618L235.274 78.085C259.595 85.7525 288.986 101.345 276.9 139.703C264.813 178.061 231.797 173.976 207.476 166.309L-22.2761 93.8686C-46.5935 86.2005 -75.3814 68.6909 -63.8987 32.2517Z';
    const pathM =
        'M-142.807 200.909C-138.632 124.969 -72.5995 112.559 -21.8227 115.354L457.904 141.709C508.685 144.498 573.181 160.068 568.787 240.003C564.393 319.939 498.58 328.349 447.798 325.559L-31.929 299.202C-82.7046 296.41 -146.983 276.845 -142.807 200.909Z';
    const pathL =
        'M-334.404 569.244C-353.603 393.393 -206.817 339.891 -89.2533 327.052L1021.33 205.724C1138.89 192.881 1292.76 204.169 1312.97 389.274C1333.18 574.38 1185.38 618.624 1067.82 631.466L-42.7695 752.791C-160.326 765.633 -315.204 745.091 -334.404 569.244Z';

    function updateInputFieldIfExists(form: HTMLFormElement | undefined, selector: string, value: string | undefined) {
        if (value && form) {
            const inputField = form.querySelector(selector) as HTMLInputElement;
            if (inputField) {
                inputField.value = value;
                inputField.dispatchEvent(new Event('input', {bubbles: true}));
            }
        }
    }
</script>

<style lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;

    .slice-form {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        min-height: 747px;
        overflow: hidden;
        position: relative;

        &--hide-in-mobile {
            display: none;

            @include mq.screen_sm(min) {
                display: flex;
            }
        }

        .slice-title {
            margin-bottom: var(--joy-core-spacing-10);
        }

        .slice-content {
            text-align: left;
        }

        &__card {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            position: relative;
            background-color: var(--joy-color-background-default);
            width: 100%;
            max-width: 600px;
            box-sizing: border-box;
            padding: var(--joy-core-spacing-9);
            box-shadow: 0 6px 24px var(--joy-color-shadow-base);
            border-radius: var(--joy-core-radius-3);

            @include mq.screen_md(max) {
                width: 100%;
            }
        }

        &__bg-shape {
            position: absolute;
            top: 0;
            left: 0;

            &__s {
                fill: var(--joy-color-primary-30);
            }

            &__m {
                fill: var(--joy-color-neutral-30);
            }

            &__l {
                fill: var(--joy-color-primary-50);
            }

            &--malt-strategy {
                .slice-form__bg-shape__s {
                    fill: var(--joy-color-tertiary-70);
                }

                .slice-form__bg-shape__l {
                    fill: var(--joy-color-secondary-30);
                }
            }
        }

        &__container {
            position: relative;
            display: inline-block;
            transition: opacity var(--joy-transition-duration-default) var(--joy-transition-timing-function);
            opacity: 0;

            &--ready {
                opacity: 1;
            }
        }

        &--ai {
            .slice-form__bg-gradient {
                position: absolute;
                top: 0;
                left: 0;
                width: 100%;
                height: 100%;

                > div {
                    position: absolute;
                    top: 50%;
                    width: 100%;
                    aspect-ratio: 1 / 1;
                }

                &__left {
                    left: -35%;
                    background: radial-gradient(circle at center, rgba(#6d287b, 0.5) 0, rgba(#6d287b, 0) 60%);
                    transform: translateY(-60%);
                }

                &__right {
                    right: -35%;
                    background: radial-gradient(circle at center, rgba(#fb2323, 0.5) 0, rgba(#fb2323, 0) 60%);
                    transform: translateY(-40%);
                }
            }

            .hubspot-form {
                input[type='submit'] {
                    background: var(--joy-color-ai-gradient-70);
                    border: none !important;
                }
            }
        }

        &--primary {
            background-color: var(--joy-color-neutral-20);
            background-image: url('//dam.malt.com/cms/shapes/hubspot-form.svg');
            background-size: cover;
            background-position: left top;
            background-repeat: no-repeat;
            min-height: max-content;

            .slice-form__bg-shape {
                display: none;
            }

            .actions {
                .hs-button {
                    &.primary {
                        background-color: var(--joy-color-primary-50);
                        color: var(--joy-color-neutral-0);
                        border: 2px solid transparent;

                        &:hover {
                            background-color: var(--joy-color-primary-70);
                        }
                    }
                }
            }

            @include mq.screen_md(min) {
                .slice-form__container {
                    width: 100%;
                }

                .slice-form__card {
                    display: flex;
                    flex-direction: row;
                    align-items: flex-start;
                    justify-content: space-between;
                    gap: var(--joy-core-spacing-12);
                    padding: 0;
                    margin: 0;
                    width: 100%;
                    max-width: 100%;
                    background-color: transparent;
                    box-shadow: none;

                    .hubspot-form {
                        background-color: var(--joy-color-background-default);
                        border-radius: var(--joy-core-radius-4);
                        padding: var(--joy-core-spacing-9);
                    }

                    .actions {
                        justify-content: flex-end;
                    }
                }
            }
        }
    }
</style>
